import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './token.module.scss';
import { API_BASE_URL } from '../../api';
import {
  buildFixedBudgetTemplateRows,
  inferBudgetItemTypeFromCode,
  sortBudgetRowsByCode as sortBudgetRowsByFixedCode,
  toCanonicalBudgetCode
} from '../budget/budgetTemplate';
import {
  normalizeBudgetFiscalYearLabel,
  normalizeBudgetSubmissionFiscalYear
} from '../budget/fiscalYearUtils';

const MASTER_ITEMS_API = `${API_BASE_URL}/master-expenditure-items/`;
const SAVE_BUDGET_API = `${API_BASE_URL}/budgets/bulk-create/`;
const BUDGET_POPUP_DRAFT_PREFIX = 'budget_popup_draft_v1';

const inferItemTypeFromCode = (codeNumber, explicitType = '') => (
  inferBudgetItemTypeFromCode(codeNumber, explicitType)
);

const inferItemTypeFromRow = (row = {}) => (
  inferItemTypeFromCode(row?.codeNumber, row?.itemType)
);

const createBudgetRow = (id, itemType = 'capital', codeNumber = '', expenseTitle = '') => ({
  id,
  codeNumber,
  expenseTitle,
  estimatedBudget: '',
  kaifiyat: '',
  itemType
});

const createDefaultBudgetRows = () => (
  buildFixedBudgetTemplateRows().map((item, index) => createBudgetRow(
    index + 1,
    item.item_type || inferItemTypeFromCode(item.code_number),
    item.code_number || '',
    item.expense_title || ''
  ))
);

const normalizeBudgetRows = (rows = []) => {
  const normalizedRows = Array.isArray(rows)
    ? rows.map((row) => ({
        ...row,
        itemType: row.itemType || inferItemTypeFromCode(row.codeNumber)
      }))
    : [];

  return normalizedRows;
};

const parseBudgetValue = (value) => {
  const cleaned = String(value || '').replace(/,/g, '').trim();
  if (!cleaned) {
    return 0;
  }
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasBudgetRowContent = (row = {}) => (
  Boolean(
    String(row.codeNumber || '').trim() ||
    String(row.expenseTitle || '').trim() ||
    parseBudgetValue(row.estimatedBudget) > 0 ||
    String(row.kaifiyat || '').trim()
  )
);

const formatBudgetNumber = (value) => {
  const numericValue = parseBudgetValue(value);
  if (!numericValue) {
    return '';
  }
  return numericValue.toLocaleString('en-IN');
};

const sortBudgetRowsByCode = (rows = []) => sortBudgetRowsByFixedCode(
  rows,
  (row) => row?.codeNumber || ''
);

const createBudgetPopupDraftKey = (context) => {
  if (!context?.institutionId || !context?.fiscalYearId) {
    return '';
  }

  const tokenPart = String(context.sourceToken || context.budgetToken || 'direct').trim().toUpperCase();
  return [
    BUDGET_POPUP_DRAFT_PREFIX,
    context.institutionId,
    context.fiscalYearId,
    tokenPart || 'direct'
  ].join(':');
};

const TokenSearchPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBudgetEditPopup, setShowBudgetEditPopup] = useState(false);
  const [budgetPopupLoading, setBudgetPopupLoading] = useState(false);
  const [budgetPopupSaving, setBudgetPopupSaving] = useState(false);
  const [budgetPopupRows, setBudgetPopupRows] = useState([]);
  const [budgetPopupContext, setBudgetPopupContext] = useState(null);
  const [budgetPopupMessage, setBudgetPopupMessage] = useState('');
  const [budgetPopupMessageType, setBudgetPopupMessageType] = useState('info');
  const skipNextPopupDraftSaveRef = useRef(false);
  const budgetPopupDraftKey = useMemo(
    () => createBudgetPopupDraftKey(budgetPopupContext),
    [budgetPopupContext]
  );

  const getOptionalAuthHeaders = () => {
    const authToken = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Token ${authToken}` } : {})
    };
  };

  const buildRowsFromMasterItems = (masterItems = []) => {
    const templateRows = buildFixedBudgetTemplateRows(masterItems);
    return normalizeBudgetRows(templateRows.map((item, index) => createBudgetRow(
      index + 1,
      item.item_type || inferItemTypeFromCode(item.code_number),
      item.code_number || '',
      item.expense_title || ''
    )));
  };

  const mergeRowsWithTemplate = (inputRows = [], templateRows = []) => {
    const normalizedRows = normalizeBudgetRows(inputRows);
    const normalizedTemplates = normalizeBudgetRows(templateRows);

    if (normalizedTemplates.length === 0) {
      return normalizedRows;
    }

    const byCode = new Map(
      normalizedRows
        .filter((row) => String(row.codeNumber || '').trim())
        .map((row) => [toCanonicalBudgetCode(row.codeNumber), row])
    );

    const mergedRows = normalizedTemplates.map((templateRow, index) => {
      const key = toCanonicalBudgetCode(templateRow.codeNumber);
      const matchedRow = key ? byCode.get(key) : null;
      return {
        id: templateRow.id || index + 1,
        codeNumber: templateRow.codeNumber || matchedRow?.codeNumber || '',
        expenseTitle: templateRow.expenseTitle || matchedRow?.expenseTitle || '',
        estimatedBudget: matchedRow?.estimatedBudget ?? '',
        kaifiyat: matchedRow?.kaifiyat || '',
        itemType: templateRow.itemType || inferItemTypeFromCode(templateRow.codeNumber)
      };
    });

    const templateCodes = new Set(
      normalizedTemplates.map((row) => toCanonicalBudgetCode(row.codeNumber)).filter(Boolean)
    );
    const customRows = normalizedRows.filter((row) => {
      const key = toCanonicalBudgetCode(row.codeNumber);
      return key ? !templateCodes.has(key) : hasBudgetRowContent(row);
    });

    return [...mergedRows, ...customRows];
  };

  const getBudgetDataWithFallbackItems = (data, fallbackItems = []) => {
    const inlineFallbackItems = Array.isArray(data?.fallback_items) ? data.fallback_items : [];
    const sourceFallbackItems = inlineFallbackItems.length > 0 ? inlineFallbackItems : fallbackItems;
    if (!Array.isArray(sourceFallbackItems) || sourceFallbackItems.length === 0) {
      return data;
    }

    return {
      ...data,
      fallback_items: sourceFallbackItems.map((item) => ({
        code_number: item?.code_number || '',
        expense_title: item?.expense_title || '',
        item_type: item?.item_type || inferItemTypeFromCode(item?.code_number)
      }))
    };
  };

  const mapBudgetSubmissionItemsToRows = (items = []) => normalizeBudgetRows(
    (Array.isArray(items) ? items : []).map((item, index) => ({
      id: item?.id || index + 1,
      codeNumber: item?.code_number || '',
      expenseTitle: item?.expense_title || '',
      estimatedBudget: item?.estimated_budget || '',
      kaifiyat: item?.remarks || '',
      itemType: inferItemTypeFromCode(item?.code_number)
    }))
  );

  const getBudgetRowsForDisplay = (data, fallbackItems = []) => {
    const resolvedData = getBudgetDataWithFallbackItems(data, fallbackItems);
    const normalizedSubmission = normalizeBudgetSubmissionFiscalYear(resolvedData?.budget_submission);
    const submissionRows = mapBudgetSubmissionItemsToRows(
      normalizedSubmission?.budget_items || []
    );
    const templateRows = buildRowsFromMasterItems(resolvedData?.fallback_items || []);
    const mergedRows = mergeRowsWithTemplate(submissionRows, templateRows);

    if (mergedRows.length > 0) {
      return mergedRows;
    }

    if (templateRows.length > 0) {
      return templateRows;
    }

    return submissionRows;
  };

  const openBudgetEditPopup = async (data) => {
    const budgetSubmission = normalizeBudgetSubmissionFiscalYear(data?.budget_submission);
    if (!budgetSubmission?.institution || !budgetSubmission?.fiscal_year) {
      setError('बजेट संस्था वा आर्थिक वर्ष जानकारी भेटिएन');
      return;
    }

    setShowBudgetEditPopup(true);
    setBudgetPopupLoading(true);
    setBudgetPopupMessage('');
    setBudgetPopupMessageType('info');

    const sourceToken = data?.source_token || budgetSubmission?.token || token.trim().toUpperCase();
    const popupAccessType = data?.access_type || 'edit';
    const isViewMode = popupAccessType === 'view';

    let rowsFromData = mapBudgetSubmissionItemsToRows(budgetSubmission.budget_items || []);

    let masterTemplateRows = [];
    try {
      const response = await fetch(MASTER_ITEMS_API, {
        headers: getOptionalAuthHeaders()
      });
      const result = await response.json();
      if (result.status === 'success' && Array.isArray(result.data) && result.data.length > 0) {
        masterTemplateRows = buildRowsFromMasterItems(result.data);
      }
    } catch (fetchError) {
      console.error('Error loading master items for popup:', fetchError);
    }

    const fallbackTemplateRows = buildRowsFromMasterItems(data?.fallback_items || []);
    const templateRows = masterTemplateRows.length > 0 ? masterTemplateRows : fallbackTemplateRows;
    rowsFromData = mergeRowsWithTemplate(rowsFromData, templateRows);
    if (rowsFromData.length === 0) {
      rowsFromData = templateRows.length > 0 ? templateRows : createDefaultBudgetRows();
    }

    const popupContext = {
      institutionId: budgetSubmission.institution,
      institutionName: budgetSubmission.institution_name || '',
      fiscalYearId: budgetSubmission.fiscal_year,
      fiscalYearName: normalizeBudgetFiscalYearLabel(budgetSubmission.fiscal_year_display || ''),
      budgetToken: budgetSubmission.token || '',
      sourceToken,
      accessType: popupAccessType,
      isPrimaryBudgetResult: data?.entity_type === 'budget'
    };

    const popupDraftKey = createBudgetPopupDraftKey(popupContext);
    if (popupDraftKey && !isViewMode) {
      try {
        const savedDraft = localStorage.getItem(popupDraftKey);
        if (savedDraft) {
          const parsedDraft = JSON.parse(savedDraft);
          const savedRows = Array.isArray(parsedDraft?.rows) ? parsedDraft.rows : [];
          if (savedRows.length > 0) {
            rowsFromData = mergeRowsWithTemplate(
              normalizeBudgetRows(savedRows),
              rowsFromData
            );
            setBudgetPopupMessage('स्थानीय ड्राफ्ट फेला पर्यो, पुनर्स्थापना गरियो');
            setBudgetPopupMessageType('success');
          }
        }
      } catch (storageError) {
        console.error('Failed to restore budget popup draft:', storageError);
      }
    }

    setBudgetPopupRows(rowsFromData);
    setBudgetPopupContext(popupContext);
    setBudgetPopupLoading(false);
  };

  const handleBudgetPopupInputChange = (rowId, field, value) => {
    setBudgetPopupRows((prevRows) => prevRows.map((row) => (
      row.id === rowId
        ? {
            ...row,
            [field]: value,
            ...(field === 'codeNumber' ? { itemType: inferItemTypeFromCode(value) } : {})
          }
        : row
    )));
  };

  const handleBudgetPopupAddRow = () => {
    setBudgetPopupRows((prevRows) => {
      const numericIds = prevRows
        .map((row) => Number.parseInt(row?.id, 10))
        .filter((id) => Number.isFinite(id));
      const nextId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1;
      return [
        ...prevRows,
        createBudgetRow(nextId, 'capital')
      ];
    });
  };

  const handleCloseBudgetPopup = useCallback(() => {
    setShowBudgetEditPopup(false);
    setBudgetPopupRows([]);
    setBudgetPopupContext(null);
    setBudgetPopupMessage('');
  }, []);

  const handleBudgetPopupSave = async () => {
    if (!budgetPopupContext) {
      return;
    }

    const items = budgetPopupRows
      .filter((row) => hasBudgetRowContent(row))
      .map((row) => ({
        code_number: row.codeNumber.trim(),
        expense_title: row.expenseTitle.trim(),
        estimated_budget: parseBudgetValue(row.estimatedBudget),
        remarks: row.kaifiyat.trim()
      }));

    if (items.length === 0) {
      setBudgetPopupMessage('कृपया कम्तिमा एउटा पंक्ति भर्नुहोस्');
      setBudgetPopupMessageType('error');
      return;
    }

    try {
      setBudgetPopupSaving(true);
      setBudgetPopupMessage('');

      const requestBody = {
        institution_id: budgetPopupContext.institutionId,
        fiscal_year_id: budgetPopupContext.fiscalYearId,
        items
      };

      if (budgetPopupContext.sourceToken) {
        requestBody.source_token = budgetPopupContext.sourceToken;
      } else if (budgetPopupContext.budgetToken) {
        requestBody.token = budgetPopupContext.budgetToken;
      }

      const response = await fetch(SAVE_BUDGET_API, {
        method: 'POST',
        headers: getOptionalAuthHeaders(),
        body: JSON.stringify(requestBody)
      });
      const result = await response.json();

      if (result.status !== 'success') {
        setBudgetPopupMessage(result.message || 'बजेट सेभ गर्न समस्या भयो');
        setBudgetPopupMessageType('error');
        return;
      }

      const returnedToken = result?.data?.token || budgetPopupContext.budgetToken || '';
      const updatedBudgetSubmission = {
        id: null,
        institution: budgetPopupContext.institutionId,
        institution_name: budgetPopupContext.institutionName,
        fiscal_year: budgetPopupContext.fiscalYearId,
        fiscal_year_display: budgetPopupContext.fiscalYearName,
        token: returnedToken,
        remarks: '',
        status: 'draft',
        budget_items: items,
        total_estimated_budget: items.reduce((sum, item) => sum + parseBudgetValue(item.estimated_budget), 0),
        created_at: null,
        updated_at: new Date().toISOString()
      };

      setSearchResult((prev) => {
        if (!prev) {
          return prev;
        }

        if (budgetPopupContext.isPrimaryBudgetResult) {
          return {
            ...prev,
            budget_submission: updatedBudgetSubmission
          };
        }

        return {
          ...prev,
          associated_budget: prev.associated_budget ? {
            ...prev.associated_budget,
            budget_submission: updatedBudgetSubmission
          } : prev.associated_budget
        };
      });

      setBudgetPopupContext((prev) => (prev ? {
        ...prev,
        budgetToken: returnedToken
      } : prev));

      skipNextPopupDraftSaveRef.current = true;
      if (budgetPopupDraftKey) {
        try {
          localStorage.removeItem(budgetPopupDraftKey);
        } catch (storageError) {
          console.error('Failed to clear budget popup draft:', storageError);
        }
      }

      setBudgetPopupMessage('बजेट डाटा सफलतापूर्वक सेभ भयो');
      setBudgetPopupMessageType('success');
    } catch (saveError) {
      console.error('Error saving budget popup data:', saveError);
      setBudgetPopupMessage('बजेट सेभ गर्न समस्या भयो');
      setBudgetPopupMessageType('error');
    } finally {
      setBudgetPopupSaving(false);
    }
  };

  const persistBudgetTokenState = (data, accessType) => {
    try {
      sessionStorage.setItem('allowBudgetTokenAccess', 'true');
      sessionStorage.setItem('budgetTokenSearchState', JSON.stringify({
        fromTokenSearch: true,
        accessType,
        tokenData: data
      }));
    } catch (e) {
      console.error('Failed to persist budget token access state:', e);
    }
  };

  const clearBudgetTokenState = () => {
    try {
      sessionStorage.removeItem('allowBudgetTokenAccess');
      sessionStorage.removeItem('budgetTokenSearchState');
    } catch (e) {
      console.error('Failed to clear budget token access state:', e);
    }
  };

  useEffect(() => {
    if (
      !showBudgetEditPopup ||
      !budgetPopupDraftKey ||
      budgetPopupContext?.accessType === 'view' ||
      budgetPopupLoading
    ) {
      return undefined;
    }

    if (skipNextPopupDraftSaveRef.current) {
      skipNextPopupDraftSaveRef.current = false;
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(budgetPopupDraftKey, JSON.stringify({
          rows: budgetPopupRows,
          updated_at: new Date().toISOString()
        }));
      } catch (storageError) {
        console.error('Failed to save budget popup draft:', storageError);
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [
    showBudgetEditPopup,
    budgetPopupDraftKey,
    budgetPopupContext,
    budgetPopupRows,
    budgetPopupLoading
  ]);

  useEffect(() => {
    if (!showBudgetEditPopup) {
      return undefined;
    }

    const handleEscClose = (event) => {
      if (event.key === 'Escape') {
        handleCloseBudgetPopup();
      }
    };

    window.addEventListener('keydown', handleEscClose);
    return () => {
      window.removeEventListener('keydown', handleEscClose);
    };
  }, [showBudgetEditPopup, handleCloseBudgetPopup]);

  const handleSearch = async () => {
    if (!token.trim()) {
      setError('Please enter a token');
      return;
    }

    setLoading(true);
    setError('');
    setSearchResult(null);
    setShowBudgetEditPopup(false);
    setBudgetPopupRows([]);
    setBudgetPopupContext(null);
    setBudgetPopupMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/token/search/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: token.trim().toUpperCase() }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSearchResult(data.data);
      } else {
        setError(data.message || 'Token not found');
      }
    } catch (err) {
      setError('Error searching for token. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleViewData = (data, fallbackItems = []) => {
    const isBudget = data.entity_type === 'budget' || !!data.budget_submission;
    const resolvedData = isBudget ? getBudgetDataWithFallbackItems(data, fallbackItems) : data;

    // Navigate to view mode based on data type
    const state = {
      tokenData: resolvedData,
      accessType: 'view',
      fromTokenSearch: true
    };

    if (!isBudget) {
      clearBudgetTokenState();
      sessionStorage.setItem('allowTokenAccess', 'true');
      navigate('/capital-expenditure/new-data', { state });
      return;
    }

    persistBudgetTokenState(resolvedData, 'view');
    openBudgetEditPopup({
      ...resolvedData,
      access_type: 'view'
    });
  };

  const handleEditData = (data, fallbackItems = []) => {
    const isBudget = data.entity_type === 'budget' || !!data.budget_submission;
    const resolvedData = isBudget ? getBudgetDataWithFallbackItems(data, fallbackItems) : data;

    // Navigate to edit mode based on data type
    const state = {
      tokenData: resolvedData,
      accessType: 'edit',
      fromTokenSearch: true
    };

    if (!isBudget) {
      clearBudgetTokenState();
      sessionStorage.setItem('allowTokenAccess', 'true');
      navigate('/capital-expenditure/new-data', { state });
      return;
    }

    persistBudgetTokenState(resolvedData, 'edit');
    openBudgetEditPopup({
      ...resolvedData,
      access_type: 'edit'
    });
  };

  const handleBudgetDownload = (data) => {
    const budgetSubmission = data?.budget_submission;
    if (!budgetSubmission) {
      setError('डाउनलोड गर्न बजेट डाटा फेला परेन');
      return;
    }

    const rowsForExport = getBudgetRowsForDisplay(data);
    if (rowsForExport.length === 0) {
      setError('डाउनलोड गर्न बजेट डाटा फेला परेन');
      return;
    }

    const capitalRows = sortBudgetRowsByCode(
      rowsForExport.filter((row) => inferItemTypeFromRow(row) === 'capital')
    );
    const operationalRows = sortBudgetRowsByCode(
      rowsForExport.filter((row) => inferItemTypeFromRow(row) === 'operational')
    );
    const capitalTotal = capitalRows.reduce((sum, row) => sum + parseBudgetValue(row?.estimatedBudget), 0);
    const operationalTotal = operationalRows.reduce((sum, row) => sum + parseBudgetValue(row?.estimatedBudget), 0);
    const grandTotal = capitalTotal + operationalTotal;
    const toCsvBudgetValue = (value) => {
      const numericValue = parseBudgetValue(value);
      return numericValue ? numericValue : '';
    };

    const rowsForCsv = [
      ['कोड नं.', 'खर्च शिर्षक', 'अनुमानित बजेट', 'कैफियत']
    ];

    rowsForCsv.push(['', 'पुँजीगत खर्च', '', '']);
    capitalRows.forEach((row) => {
      rowsForCsv.push([
        row?.codeNumber || '',
        row?.expenseTitle || '',
        toCsvBudgetValue(row?.estimatedBudget),
        row?.kaifiyat || ''
      ]);
    });
    rowsForCsv.push(['', 'जम्मा पुँजीगत', toCsvBudgetValue(capitalTotal), '']);

    rowsForCsv.push(['', 'संचालन खर्च', '', '']);
    operationalRows.forEach((row) => {
      rowsForCsv.push([
        row?.codeNumber || '',
        row?.expenseTitle || '',
        toCsvBudgetValue(row?.estimatedBudget),
        row?.kaifiyat || ''
      ]);
    });
    rowsForCsv.push(['', 'जम्मा संचालन', toCsvBudgetValue(operationalTotal), '']);
    rowsForCsv.push(['', 'जम्मा संचालन र पुँजीगत खर्च', toCsvBudgetValue(grandTotal), '']);

    const csvContent = `\uFEFF${rowsForCsv.map((row) => (
      row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')
    )).join('\n')}`;

    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const csvUrl = window.URL.createObjectURL(csvBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = csvUrl;

    const institutionSlug = String(budgetSubmission?.institution_name || 'institution')
      .replace(/\s+/g, '_')
      .replace(/[^\w-]/g, '');
    const fiscalYearSlug = String(normalizeBudgetFiscalYearLabel(budgetSubmission?.fiscal_year_display || 'fy'))
      .replace(/\s+/g, '_')
      .replace(/[^\w-]/g, '');
    downloadLink.download = `budget_${institutionSlug}_${fiscalYearSlug}.csv`;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(csvUrl);
  };

  const handleGoBack = () => {
    sessionStorage.removeItem('allowTokenAccess');
    clearBudgetTokenState();
    navigate('/login');
  };

  const isBudgetResult = searchResult?.entity_type === 'budget' || !!searchResult?.budget_submission;
  const previewData = isBudgetResult ? searchResult?.budget_submission : searchResult?.capital_expenditure;
  const budgetItems = isBudgetResult ? getBudgetRowsForDisplay(searchResult) : [];
  const totalEstimatedBudget = budgetItems.reduce((sum, item) => {
    const value = Number(item?.estimatedBudget ?? item?.estimated_budget ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const associatedBudget = !isBudgetResult ? searchResult?.associated_budget : null;
  const associatedBudgetSubmission = associatedBudget?.budget_submission || null;
  const associatedBudgetItems = associatedBudget
    ? getBudgetRowsForDisplay(
        getBudgetDataWithFallbackItems(
          associatedBudget,
          searchResult?.capital_expenditure?.expenditure_items || []
        )
      )
    : [];
  const associatedBudgetTotal = associatedBudgetItems.reduce((sum, item) => {
    const value = Number(item?.estimatedBudget ?? item?.estimated_budget ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const popupCapitalRows = sortBudgetRowsByCode(
    budgetPopupRows.filter((row) => inferItemTypeFromRow(row) === 'capital')
  );
  const popupOperationalRows = sortBudgetRowsByCode(
    budgetPopupRows.filter((row) => inferItemTypeFromRow(row) === 'operational')
  );
  const popupCapitalTotal = popupCapitalRows.reduce((sum, row) => sum + parseBudgetValue(row?.estimatedBudget), 0);
  const popupOperationalTotal = popupOperationalRows.reduce((sum, row) => sum + parseBudgetValue(row?.estimatedBudget), 0);
  const popupGrandTotal = popupCapitalTotal + popupOperationalTotal;

  return (
    <div className={styles.tokenSearchPageContainer}>
      <div className={styles.pageHeader}>
        <button 
          className={styles.backButton}
          onClick={handleGoBack}
          title="फिर्ता जानुहोस्"
        >
          ← फिर्ता जानुहोस्
        </button>
        <h1>🔍 टोकनबाट डाटा खोज्नुहोस्</h1>
        <p>आफ्नो डाटा हेर्न र सम्पादन गर्न टोकन प्रविष्ट गर्नुहोस्</p>
      </div>

      <div className={styles.tokenSearchContainer}>
        <div className={styles.searchForm}>
          <div className={styles.inputGroup}>
            <label htmlFor="token">टोकन:</label>
            <input
              type="text"
              id="token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="उदाहरण: AB123456"
              maxLength={8}
              className={styles.tokenInput}
            />
            <small>टोकन फरमेट: 2 अक्षर + 6 अंक (जस्तै: AB123456)</small>
          </div>
          
          <button
            onClick={handleSearch}
            disabled={loading || !token.trim()}
            className={styles.searchButton}
          >
            {loading ? 'खोज्दै...' : '🔍 खोज्नुहोस्'}
          </button>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            ❌ {error}
          </div>
        )}

        {searchResult && (
          <div className={styles.searchResult}>
            <div className={styles.resultHeader}>
              <h3>✅ डाटा फेला पर्यो!</h3>
              <div className={styles.accessInfo}>
                <span className={`${styles.accessBadge} ${styles[searchResult.access_type]}`}>
                  {searchResult.access_type === 'view' ? '👁️ हेर्ने मात्र' :
                   searchResult.access_type === 'edit' ? '✏️ सम्पादन गर्न सकिन्छ' :
                   '🔧 प्रशासक पहुँच'}
                </span>
                {searchResult.expires_at && (
                  <span className={styles.expiryInfo}>
                    ⏰ म्याद: {new Date(searchResult.expires_at).toLocaleDateString('ne-NP')}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.dataPreview}>
              <h4>संस्था जानकारी:</h4>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <strong>संस्था:</strong> {previewData?.institution_name || 'N/A'}
                </div>
                <div className={styles.infoItem}>
                  <strong>आर्थिक वर्ष:</strong> {normalizeBudgetFiscalYearLabel(previewData?.fiscal_year_display || '') || 'N/A'}
                </div>
                {searchResult.opened_current_year && (
                  <div className={`${styles.infoItem} ${styles.currentYearNotice}`}>
                    <strong>चालु वर्ष:</strong> फारम {searchResult.working_fiscal_year_display || '२०८३/०८४'} मा खोलिन्छ।
                    {searchResult.source_fiscal_year_display
                      ? ` टोकन ${searchResult.source_fiscal_year_display} को हो; पुरानो डाटा सुरक्षित छ। फारमको माथिबाट आर्थिक वर्ष बदल्न सकिन्छ।`
                      : ''}
                  </div>
                )}
                <div className={styles.infoItem}>
                  <strong>स्थिति:</strong> {previewData?.status || 'N/A'}
                </div>
                <div className={styles.infoItem}>
                  <strong>सिर्जना मिति:</strong> {previewData?.created_at ? new Date(previewData.created_at).toLocaleDateString('ne-NP') : 'N/A'}
                </div>
                {isBudgetResult && (
                  <div className={styles.infoItem}>
                    <strong>बजेट पंक्ति संख्या:</strong> {budgetItems.length}
                  </div>
                )}
                {isBudgetResult && (
                  <div className={styles.infoItem}>
                    <strong>जम्मा अनुमानित बजेट:</strong> {totalEstimatedBudget ? totalEstimatedBudget.toLocaleString('en-IN') : '0'}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.actionButtons}>
              {searchResult.can_view && (
                <button 
                  className={`${styles.actionButton} ${styles.viewButton}`}
                  onClick={() => handleViewData(searchResult)}
                >
                  {isBudgetResult ? '📊 पूर्ण बजेट डाटा हेर्नुहोस्' : '📊 पूर्ण डाटा हेर्नुहोस्'}
                </button>
              )}
              {(searchResult.can_edit || isBudgetResult) && (
                <button 
                  className={`${styles.actionButton} ${styles.editButton}`}
                  onClick={() => {
                    if (searchResult.can_edit) {
                      handleEditData(searchResult);
                    }
                  }}
                  disabled={!searchResult.can_edit}
                  title={searchResult.can_edit ? '' : 'यो टोकनमा edit अनुमति छैन'}
                >
                  {isBudgetResult ? '✏️ बजेट डाटा सम्पादन गर्नुहोस्' : '✏️ डाटा सम्पादन गर्नुहोस्'}
                </button>
              )}
              {isBudgetResult && searchResult.can_view && (
                <button
                  className={`${styles.actionButton} ${styles.viewButton}`}
                  onClick={() => handleBudgetDownload(searchResult)}
                >
                  ⬇️ बजेट डाउनलोड गर्नुहोस्
                </button>
              )}
            </div>
          </div>
        )}

        {associatedBudgetSubmission && (
          <div className={styles.searchResult}>
            <div className={styles.resultHeader}>
              <h3>✅ बजेट डाटा फेला पर्यो!</h3>
              <div className={styles.accessInfo}>
                <span className={`${styles.accessBadge} ${styles[associatedBudget.access_type || 'edit']}`}>
                  {(associatedBudget.access_type || 'edit') === 'view' ? '👁️ हेर्ने मात्र' :
                   (associatedBudget.access_type || 'edit') === 'edit' ? '✏️ सम्पादन गर्न सकिन्छ' :
                   '🔧 प्रशासक पहुँच'}
                </span>
              </div>
            </div>

            <div className={styles.dataPreview}>
              <h4>बजेट जानकारी:</h4>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <strong>संस्था:</strong> {associatedBudgetSubmission?.institution_name || 'N/A'}
                </div>
                <div className={styles.infoItem}>
                  <strong>आर्थिक वर्ष:</strong> {normalizeBudgetFiscalYearLabel(associatedBudgetSubmission?.fiscal_year_display || '') || 'N/A'}
                </div>
                <div className={styles.infoItem}>
                  <strong>स्थिति:</strong> {associatedBudgetSubmission?.status || 'N/A'}
                </div>
                <div className={styles.infoItem}>
                  <strong>सिर्जना मिति:</strong> {associatedBudgetSubmission?.created_at ? new Date(associatedBudgetSubmission.created_at).toLocaleDateString('ne-NP') : 'N/A'}
                </div>
                <div className={styles.infoItem}>
                  <strong>बजेट पंक्ति संख्या:</strong> {associatedBudgetItems.length}
                </div>
                <div className={styles.infoItem}>
                  <strong>जम्मा अनुमानित बजेट:</strong> {associatedBudgetTotal ? associatedBudgetTotal.toLocaleString('en-IN') : '0'}
                </div>
              </div>
            </div>

            <div className={styles.actionButtons}>
              {associatedBudget.can_view && (
                <button
                  className={`${styles.actionButton} ${styles.viewButton}`}
                  onClick={() => handleViewData(
                    associatedBudget,
                    searchResult?.capital_expenditure?.expenditure_items || []
                  )}
                >
                  📊 पूर्ण बजेट डाटा हेर्नुहोस्
                </button>
              )}
              {associatedBudgetSubmission && (
                <button
                  className={`${styles.actionButton} ${styles.editButton}`}
                  onClick={() => {
                    if (associatedBudget.can_edit) {
                      handleEditData(
                        associatedBudget,
                        searchResult?.capital_expenditure?.expenditure_items || []
                      );
                    }
                  }}
                  disabled={!associatedBudget.can_edit}
                  title={associatedBudget.can_edit ? '' : 'यो टोकनमा edit अनुमति छैन'}
                >
                  ✏️ बजेट डाटा सम्पादन गर्नुहोस्
                </button>
              )}
              {associatedBudget.can_view && (
                <button
                  className={`${styles.actionButton} ${styles.viewButton}`}
                  onClick={() => handleBudgetDownload(
                    getBudgetDataWithFallbackItems(
                      associatedBudget,
                      searchResult?.capital_expenditure?.expenditure_items || []
                    )
                  )}
                >
                  ⬇️ बजेट डाउनलोड गर्नुहोस्
                </button>
              )}
            </div>
          </div>
        )}

        {showBudgetEditPopup && (
          <div className={styles.budgetPopupOverlay} onClick={handleCloseBudgetPopup}>
            <div
              className={styles.budgetPopupContainer}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className={styles.budgetPopupHeader}>
                <h3>
                  {budgetPopupContext?.accessType === 'view'
                    ? '📊 पूर्ण बजेट डाटा हेर्नुहोस्'
                    : '✏️ बजेट डाटा सम्पादन गर्नुहोस्'}
                </h3>
                <button
                  type="button"
                  className={styles.budgetPopupClose}
                  onClick={handleCloseBudgetPopup}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className={styles.budgetPopupMeta}>
                <span><strong>संस्था:</strong> {budgetPopupContext?.institutionName || 'N/A'}</span>
                <span><strong>आर्थिक वर्ष:</strong> {normalizeBudgetFiscalYearLabel(budgetPopupContext?.fiscalYearName || '') || 'N/A'}</span>
              </div>

              {budgetPopupLoading ? (
                <div className={styles.budgetPopupLoading}>बजेट फारम लोड हुँदैछ...</div>
              ) : (
                <div className={styles.budgetPopupTableWrapper}>
                  <table className={styles.budgetPopupTable}>
                    <thead>
                      <tr>
                        <th>कोड नं.</th>
                        <th>खर्च शिर्षक</th>
                        <th>अनुमानित बजेट</th>
                        <th>कैफियत</th>
                      </tr>
                    </thead>
                    <tbody>
                      {budgetPopupRows.length === 0 ? (
                        <tr>
                          <td colSpan="4">भरेको बजेट पंक्ति छैन</td>
                        </tr>
                      ) : (
                        <>
                          <tr className={styles.budgetPopupCategoryRow}>
                            <td colSpan="4">पुँजीगत खर्च</td>
                          </tr>
                          {popupCapitalRows.map((row) => (
                            <tr key={`popup-capital-${row.id}`}>
                              <td>
                                <input
                                  type="text"
                                  value={row.codeNumber}
                                  onChange={(e) => handleBudgetPopupInputChange(row.id, 'codeNumber', e.target.value)}
                                  readOnly={budgetPopupContext?.accessType === 'view'}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.expenseTitle}
                                  onChange={(e) => handleBudgetPopupInputChange(row.id, 'expenseTitle', e.target.value)}
                                  readOnly={budgetPopupContext?.accessType === 'view'}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.estimatedBudget}
                                  onChange={(e) => handleBudgetPopupInputChange(row.id, 'estimatedBudget', e.target.value)}
                                  readOnly={budgetPopupContext?.accessType === 'view'}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.kaifiyat}
                                  onChange={(e) => handleBudgetPopupInputChange(row.id, 'kaifiyat', e.target.value)}
                                  readOnly={budgetPopupContext?.accessType === 'view'}
                                />
                              </td>
                            </tr>
                          ))}
                          <tr className={styles.budgetPopupTotalRow}>
                            <td></td>
                            <td><strong>जम्मा पुँजीगत</strong></td>
                            <td><strong>{formatBudgetNumber(popupCapitalTotal)}</strong></td>
                            <td></td>
                          </tr>

                          <tr className={styles.budgetPopupCategoryRow}>
                            <td colSpan="4">संचालन खर्च</td>
                          </tr>
                          {popupOperationalRows.map((row) => (
                            <tr key={`popup-operational-${row.id}`}>
                              <td>
                                <input
                                  type="text"
                                  value={row.codeNumber}
                                  onChange={(e) => handleBudgetPopupInputChange(row.id, 'codeNumber', e.target.value)}
                                  readOnly={budgetPopupContext?.accessType === 'view'}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.expenseTitle}
                                  onChange={(e) => handleBudgetPopupInputChange(row.id, 'expenseTitle', e.target.value)}
                                  readOnly={budgetPopupContext?.accessType === 'view'}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={row.estimatedBudget}
                                  onChange={(e) => handleBudgetPopupInputChange(row.id, 'estimatedBudget', e.target.value)}
                                  readOnly={budgetPopupContext?.accessType === 'view'}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={row.kaifiyat}
                                  onChange={(e) => handleBudgetPopupInputChange(row.id, 'kaifiyat', e.target.value)}
                                  readOnly={budgetPopupContext?.accessType === 'view'}
                                />
                              </td>
                            </tr>
                          ))}
                          <tr className={styles.budgetPopupTotalRow}>
                            <td></td>
                            <td><strong>जम्मा संचालन</strong></td>
                            <td><strong>{formatBudgetNumber(popupOperationalTotal)}</strong></td>
                            <td></td>
                          </tr>
                          <tr className={styles.budgetPopupGrandTotalRow}>
                            <td></td>
                            <td><strong>जम्मा संचालन र पुँजीगत खर्च</strong></td>
                            <td><strong>{formatBudgetNumber(popupGrandTotal)}</strong></td>
                            <td></td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {budgetPopupMessage && (
                <div className={`${styles.budgetPopupMessage} ${styles[budgetPopupMessageType]}`}>
                  {budgetPopupMessage}
                </div>
              )}

              <div className={styles.budgetPopupActions}>
                <button
                  type="button"
                  className={styles.budgetPopupCancelButton}
                  onClick={handleCloseBudgetPopup}
                >
                  बन्द गर्नुहोस्
                </button>
                {budgetPopupContext?.accessType !== 'view' && (
                  <button
                    type="button"
                    className={styles.budgetPopupCancelButton}
                    onClick={handleBudgetPopupAddRow}
                    disabled={budgetPopupSaving || budgetPopupLoading}
                  >
                    पंक्ति थप्नुहोस्
                  </button>
                )}
                {budgetPopupContext?.accessType !== 'view' && (
                  <button
                    type="button"
                    className={styles.budgetPopupSaveButton}
                    onClick={handleBudgetPopupSave}
                    disabled={budgetPopupSaving || budgetPopupLoading}
                  >
                    {budgetPopupSaving ? 'सेभ हुँदैछ...' : 'सेभ गर्नुहोस्'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className={styles.helpSection}>
          <h4>💡 सहायता:</h4>
          <ul>
            <li>टोकन आपको डाटा सुरक्षित राख्नको लागि प्रयोग गरिन्छ</li>
            <li>टोकन फरमेट: 2 अंग्रेजी अक्षर + 6 अंक (जस्तै: AB123456)</li>
            <li>यदि टोकन काम गर्दैन भने आफ्नो प्रशासकलाई सम्पर्क गर्नुहोस्</li>
            <li>टोकन सुरक्षित राख्नुहोस् र अरूलाई नदिनुहोस्</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TokenSearchPage;
